/**
 * シーンテンプレート (F2)
 *
 * 典型的なシーンタイプを選ぶと、ゾーン配置・推奨フィルター・予想 DR の初期値を自動設定。
 * 測光画面の「テンプレート」から呼び出す。適用するのは、これから置くピンの初期
 * シャドウ／ハイライトゾーン。推奨の現像方針は露出画面で計算結果と並べて比較する。
 */

import { openSheet, escapeHtml, toast, zoneToRoman, zoneCellsHtml, buzz } from './ui.js';
import { getCurrentShoot, updateCurrentShoot } from './scene.js';
import { planLabel } from './zone-calc.js';
import { FILTER_ABBR } from './gear.js';

export const TEMPLATES = [
  {
    id: 'sunrise',
    name: '朝焼け / 夕焼け',
    desc: '太陽低い、空に階調、地表に深い影',
    defaultShadowZone: 3,
    defaultHighlightZone: 8,
    expectedDR: 7,
    suggestedFilter: 'orange16',
    suggestedDevPlan: 'N-1',
    suggestedMonoPreset: 'tri-x',
    notes: 'マジックアワー: 太陽が地平線に近く、長く伸びる影と暖かい光。',
  },
  {
    id: 'overcast',
    name: '曇天 / 霧',
    desc: '低コントラスト、シャドウ豊か',
    defaultShadowZone: 4,
    defaultHighlightZone: 7,
    expectedDR: 4,
    suggestedFilter: '',
    suggestedDevPlan: 'N+1',
    suggestedMonoPreset: 'panchromatic',
    notes: 'DR 3-4 段、N+1 拡張現像でコントラスト稼ぎ。',
  },
  {
    id: 'sunny-day',
    name: '晴天順光 (標準)',
    desc: 'バランス、最も基本的なシーン',
    defaultShadowZone: 3,
    defaultHighlightZone: 8,
    expectedDR: 6,
    suggestedFilter: 'yellow8',
    suggestedDevPlan: 'N',
    suggestedMonoPreset: 'tri-x',
    notes: 'ZS の基準シーン。N 現像で問題なし。',
  },
  {
    id: 'snow-shadow',
    name: '雪景色 + 影',
    desc: '高コントラスト、ハイライト多い',
    defaultShadowZone: 4,
    defaultHighlightZone: 8,
    expectedDR: 8,
    suggestedFilter: 'yellow8',
    suggestedDevPlan: 'N-1',
    suggestedMonoPreset: 'acros',
    notes: '雪のテクスチャを残すためゾーン VIII 配置、影の深部は犠牲覚悟。',
  },
  {
    id: 'forest',
    name: '森林 / 木陰',
    desc: '緑が支配、シャドウ多め',
    defaultShadowZone: 3,
    defaultHighlightZone: 7,
    expectedDR: 5,
    suggestedFilter: 'green11',
    suggestedDevPlan: 'N',
    suggestedMonoPreset: 'tri-x',
    notes: '緑フィルターで葉を明るく持ち上げる、ZS 練習に向く題材。',
  },
  {
    id: 'architecture',
    name: '建築 (順光)',
    desc: '面の連続性、ハイライトとシャドウの矩形',
    defaultShadowZone: 3,
    defaultHighlightZone: 8,
    expectedDR: 6,
    suggestedFilter: 'yellow8',
    suggestedDevPlan: 'N',
    suggestedMonoPreset: 'tmax',
    notes: '面の中央を測光、ライズ・シフトで歪み補正を意識。',
  },
  {
    id: 'portrait-shade',
    name: '人物 (日陰)',
    desc: 'ソフト、柔らかい肌の階調',
    defaultShadowZone: 4,
    defaultHighlightZone: 7,
    expectedDR: 4,
    suggestedFilter: '',
    suggestedDevPlan: 'N+1',
    suggestedMonoPreset: 'hp5',
    notes: '日陰の肌をゾーン V-VI に。HP5+ でハイライトを伸ばす。',
  },
  {
    id: 'night-city',
    name: '夜景 / 街灯',
    desc: '極端な高 DR、街灯が突出',
    defaultShadowZone: 2,
    defaultHighlightZone: 9,
    expectedDR: 10,
    suggestedFilter: '',
    suggestedDevPlan: 'N-2',
    suggestedMonoPreset: 'acros',
    notes: 'Acros II + 三脚 + 長時間露光。街灯本体はゾーン IX-X 飛び覚悟。',
  },
  {
    id: 'silhouette',
    name: '逆光シルエット',
    desc: '主役を黒く、空にディテール',
    defaultShadowZone: 1,
    defaultHighlightZone: 8,
    expectedDR: 9,
    suggestedFilter: 'orange16',
    suggestedDevPlan: 'N-1',
    suggestedMonoPreset: 'tri-x',
    notes: '主役は意図的にゾーン I-II に。空のハイライトに合わせる。',
  },
  {
    id: 'low-key',
    name: 'ローキー (重厚)',
    desc: '画面全体が暗め、アクセント白',
    defaultShadowZone: 2,
    defaultHighlightZone: 6,
    expectedDR: 5,
    suggestedFilter: '',
    suggestedDevPlan: 'N',
    suggestedMonoPreset: 'tri-x',
    notes: 'ハイライトを抑えめに置き、シャドウ深く。N 現像 + Grade 3-4 印画紙。',
  },
  {
    id: 'high-key',
    name: 'ハイキー (軽快)',
    desc: '画面全体が明るめ、アクセント黒',
    defaultShadowZone: 6,
    defaultHighlightZone: 8,
    expectedDR: 4,
    suggestedFilter: '',
    suggestedDevPlan: 'N',
    suggestedMonoPreset: 'acros',
    notes: 'シャドウ III ではなく VI に置く特殊配置。背景白、アクセント黒。',
  },
];

export function getTemplate(id) {
  return TEMPLATES.find((t) => t.id === id) || null;
}

/** シーンに記録されたテンプレート（旧データの sceneTemplate も読む） */
export function templateOf(shoot) {
  return getTemplate(shoot?.templateId ?? shoot?.sceneTemplate);
}

export function openSceneTemplates() {
  const shoot = getCurrentShoot();
  const cur = templateOf(shoot)?.id;
  const body = document.createElement('div');
  body.innerHTML = `
    <p class="sub" style="margin:0 24px 12px;font-size:12px;line-height:1.6">これから置くピンの初期シャドウ／ハイライトゾーンを設定します。推奨の現像方針は露出画面で計算結果と比べられます。</p>
    <div style="border-top:1px solid var(--color-ink)">
      ${TEMPLATES.map((t, i) => `
        <button type="button" class="tpl-row ${t.id === cur ? 'is-on' : ''}" data-template-id="${escapeHtml(t.id)}" style="animation-delay:${i * 30}ms">
          <div class="tpl-row__range"><div class="zstrip">${zoneCellsHtml(t.defaultShadowZone, t.defaultHighlightZone, 'tpl-off')}</div><div class="en sub" style="font-size:10px;margin-top:6px">${zoneToRoman(t.defaultShadowZone)} – ${zoneToRoman(t.defaultHighlightZone)}</div></div>
          <div style="flex:1;min-width:0">
            <div style="font:500 15px/1.3 var(--font-jp-sans)">${escapeHtml(t.name)}</div>
            <div class="en" style="font:500 11px/1.4 var(--font-en);margin-top:4px">DR 約 ${t.expectedDR} 段 · ${escapeHtml(planLabel(t.suggestedDevPlan))} · ${escapeHtml(FILTER_ABBR[t.suggestedFilter] || 'フィルターなし')}</div>
            <div class="sub" style="font-size:12px;line-height:1.5;margin-top:3px">${escapeHtml(t.desc)}</div>
          </div>
          <div style="width:22px;flex:none">${t.id === cur ? '<svg class="i draw" width="20" height="20"><use href="#i-check"/></svg>' : ''}</div>
        </button>`).join('')}
    </div>`;
  const sheet = openSheet({ en: 'scene templates', title: 'シーンテンプレート', body, ruled: false, full: true });
  body.querySelectorAll('[data-template-id]').forEach((el) => {
    el.addEventListener('click', () => {
      const t = getTemplate(el.dataset.templateId);
      if (!t || !getCurrentShoot()) return;
      updateCurrentShoot({
        defaultShadowZone: t.defaultShadowZone,
        defaultHighlightZone: t.defaultHighlightZone,
        templateId: t.id,
        sceneTemplate: t.id,
      });
      buzz(8);
      toast(`${t.name}：次に置くピンは ${zoneToRoman(t.defaultShadowZone)} / ${zoneToRoman(t.defaultHighlightZone)}`);
      setTimeout(() => sheet.close(), 160);
    });
  });
}
